import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import states from 'states-us';

import EditField from './EditField';

import { regexZipCode } from '../utils/zipRegex';

export default class AddressField extends EditField {
  constructor(line1, line2, city, state, country, zip, label = '', options = {}) {
    super('address', label, options);
    this.line1 = line1;
    this.line2 = line2;
    this.city = city;
    this.state = state;
    this.country = country;
    this.zip = zip;
  }

  hasValue(data) {
    return (
      data &&
      [
        data[this.line1],
        data[this.city],
        data[this.state],
        data[this.country],
        data[this.zip]
      ].every(Boolean)
    );
  }

  validateAddressLine1(data) {
    const stateAbbreviation = data?.[this.state];
    return !!(
      data &&
      data[this.line1] &&
      data[this.city] &&
      stateAbbreviation &&
      states.find((state) => state.abbreviation === stateAbbreviation) &&
      data[this.zip] &&
      regexZipCode.test(data[this.zip]) &&
      data[this.country] &&
      data[this.country].toUpperCase() === 'US'
    );
  }

  validateAddressLine2(data) {
    const stateAbbreviation = data?.[this.state];
    return !!(
      data &&
      data[this.line1] &&
      data[this.line2] &&
      data[this.city] &&
      stateAbbreviation &&
      states.find((state) => state.abbreviation === stateAbbreviation) &&
      data[this.zip] &&
      regexZipCode.test(data[this.zip]) &&
      data[this.country] &&
      data[this.country].toUpperCase() === 'US'
    );
  }

  getValue(data) {
    if (!this.hasValue(data)) {
      return '';
    }
    const line1 = data[this.line1];
    const line2 = data[this.line2];
    const city = data[this.city];
    const state = data[this.state];
    const zip = data[this.zip];
    return `${line1} ${line2} ${city}, ${state} ${zip}`;
  }

  #renderAddressValue(data, inValidData) {
    const line1 = data[this.line1];
    const line2 = data[this.line2];
    const city = data[this.city];
    const state = data[this.state];
    const zip = data[this.zip];

    const label = (
      <Text
        color="tertiary"
        component={this.options.label?.component || 'span'}
        size={this.options.label?.variant || 'xs'}
      >
        {this.label}
      </Text>
    );
    const addressValue = (
      <>
        {line1 && (
          <Text
            component={this.options.component || 'div'}
            size={this.options.variant || 'md'}
          >
            {line1}
          </Text>
        )}
        {line2 && (
          <Text
            component={this.options.component || 'div'}
            size={this.options.variant || 'md'}
          >
            {line2}
          </Text>
        )}
        {(city || state || zip) && (
          <Text
            component={this.options.component || 'div'}
            variant={this.options.variant || 'md'}
          >
            {`${city}, ${state} ${zip}`}
          </Text>
        )}
      </>
    );

    const hasAddressValue = this.hasValue(data);
    switch (true) {
      case !hasAddressValue && inValidData: // no has data and it is a required validate field
        return (
          <>
            {label}
            <Stack
              alignItems="center"
              direction="row"
            >
              <SharedAsset
                alt=""
                src="/icon/micro/alert-fill.svg"
              />
              <Text
                aria-label="missing error"
                color="red"
                component={this.options.component || 'div'}
                marginLeft="8px"
                variant={this.options.variant || 'md'}
              >
                Contact us to update
              </Text>
            </Stack>
          </>
        );
      case hasAddressValue && inValidData:
        return (
          <Stack
            direction="column"
            spacing="6px"
          >
            {label}
            {addressValue}
            <Stack
              alignItems="center"
              direction="row"
              mt="6px"
            >
              <SharedAsset
                alt=""
                src="/icon/micro/alert-fill.svg"
              />
              <Text
                aria-label="invalid error"
                color="red"
                component={this.options.component || 'div'}
                marginLeft="8px"
                variant={this.options.variant || 'md'}
              >
                Invalid
              </Text>
            </Stack>
          </Stack>
        );

      case hasAddressValue && !inValidData: // a normal field (no required for validate)
        return (
          <Stack
            direction="column"
            spacing="6px"
          >
            {label}
            <Stack direction="column">{addressValue}</Stack>
          </Stack>
        );
      default:
        return '';
    }
  }

  getReadOnlyField(data, inValidData) {
    return (
      <Box
        key={this.label}
        mt="24px"
      >
        {this.#renderAddressValue(data, inValidData)}
      </Box>
    );
  }
}
